import { END } from 'redux-saga'
import {race, delay, fork, call, put, take, select, takeEvery, takeLatest, all} from 'redux-saga/effects';
import {captureFromCamera} from '../../Components/CapturePhotoDialog/useCapturePhoto'
import {keyStrokePlay, themeStart, themeStop} from '../../Helpers/sound'
import {
  createVadChannel,
  makeTranscriptionForm,
  normalizeTranscript,
  shouldStopMicByVoiceCommand,
  __private as sttPrivate,
} from '../../Helpers/stt'
import apiServer from '../../Api/Backend'
import {startTTSEngine} from '../../Helpers/tts'
import {IS_MOBILE_DEVICE, isMacOS} from '../../Helpers/utils'
import {
  audioStart,
  audioStop,
  audioSpeechSegmentReady,
  audioTranscribeRequest,
  audioTranscribeResult,
  audioError,
  audioSetEnabled,
  audioSetVadEnabled,
  audioSetSttEnabled,
  audioSetHearing,
  userSaidAction,
  audioSetRobotSpeaking,
  requestTextStream,
} from '../actions'
import {
  ON_APP_LOAD, PLAY_KEYSTROKE,
  TIME_TO_INIT_THEME_AFTER_KEYSTROKE,
} from '../general/consts'
import {store} from '../store'
import {
  AUDIO_START,
  AUDIO_STOP,
  AUDIO_SPEECH_SEGMENT_READY,
  AUDIO_SET_ROBOT_SPEAKING, USER_SAID, TTS_SPEAK_TOKEN,
} from './consts'
let tts = null

const MAX_AUDIO_LENGTH = 10 * 1000
const AUDIO_DEBUG = false

function* userSaidSaga({ rawText }) {
  try {
    const text = rawText?.trim()
    const botName = yield select(state => state.getIn(['general','botName']))

    if (botName === 'Arnold') {
      const targets = ['ARNOLD', 'ארנולד', 'ארנות', 'ארנוד', 'ארמוד', 'אונולד']

      const upperText = text.toUpperCase()

      const match = targets
        .map(t => ({
          target: t,
          index: upperText.indexOf(t.toUpperCase())
        }))
        .filter(m => m.index !== -1)
        .sort((a, b) => a.index - b.index)[0]

      if (match) {
        const slicedText = text.slice(match.index)
        console.log('SAGA: user said', slicedText)

        const image = yield call(captureFromCamera);
        if (!image) {
          yield put(requestTextStream(slicedText.trim()))
        } else {
          const prompt = {
            content: slicedText.trim(),
            extra: [{type: 'imageFile', base64Url: image}]
          }
          yield put(requestTextStream(prompt))
        }
      } else {
        console.log(`userSaidSaga: text "${upperText}" does not include Arnold, ignoring`)
      }
    } else {
      console.log('SAGA: user said to a non-Arnold bot', text)
      yield put(requestTextStream(text))
    }
  } catch (e) {
    console.log('error in userSaidSaga', { e })
  }
}


function* audioControllerSaga() {
  const log = (...args) => {
    if (!AUDIO_DEBUG) return
    console.log(`[AUDIO][SAGA][${new Date().toISOString()}]`, ...args)
  }

  while (true) {
    yield take(AUDIO_START)
    log('AUDIO_START 2382742 received')

    const enabled = yield select((s) => s.getIn(['audio', 'enabled']))
    const vadEnabled = yield select((s) => s.getIn(['audio', 'vadEnabled']))
    if (!enabled || !vadEnabled) {
      yield put(audioStop())
      yield put(audioSetHearing('silence'))
      continue
    }

    log('AudioSaga - Creating VAD channel...')
    const chan = yield call(createVadChannel, { debug: AUDIO_DEBUG })
    log('AudioSaga - returned from VAD channel creation (might as well continue creation in background)')

    // Hearing detector state (EMA)
    let ema = 0
    let frameCount = 0
    const EMA_ALPHA = 0.15
    const SHAKY_THRESHOLD = 0.06
    const SILENCE_THRESHOLD = 0.03
    let hearing = 'silence'

    const resetHearing = function* () {
      ema = 0
      frameCount = 0
      if (hearing !== 'silence') {
        hearing = 'silence'
        yield put(audioSetHearing('silence'))
      } else {
        // still force UI sane if you prefer:
        yield put(audioSetHearing('silence'))
      }
    }

    const updateHearingFromProb = function* (p) {
      if (typeof p !== 'number') return
      ema = EMA_ALPHA * p + (1 - EMA_ALPHA) * ema
      frameCount += 1

      if (frameCount % 10 !== 0) return

      if (hearing === 'silence' && ema >= SHAKY_THRESHOLD) {
        hearing = 'shaky'
        yield put(audioSetHearing('shaky'))
      } else if (hearing === 'shaky' && ema <= SILENCE_THRESHOLD) {
        hearing = 'silence'
        yield put(audioSetHearing('silence'))
      }
    }

    let frameSeen = 0
    let frameUsed = 0
    let lastFrameLog = Date.now()

    try {
      while (true) {
        const enabledNow = yield select((s) => s.getIn(['audio', 'enabled']))
        const vadEnabledNow = yield select((s) => s.getIn(['audio', 'vadEnabled']))
        if (!enabledNow || !vadEnabledNow) break

        const robotSpeakingNow = yield select((s) => s.getIn(['audio', 'robotSpeaking']))
        if (robotSpeakingNow) {
          // IMPORTANT: do not stop/close VAD; just keep UI state sane and drain events.
          yield* resetHearing()
        }

        const { evt, stopped } = yield race({
          evt: take(chan),
          stopped: take(AUDIO_STOP),
        })

        if (stopped) break
        if (evt === END) {
          log('channel END received')
          break
        }
        if (!evt) continue

        if (evt.type === 'error') {
          yield put(audioError(evt.error))
          break
        }

        // Gate: robot speaking => ignore all VAD events (but we ARE draining the channel).
        if (robotSpeakingNow) {
          continue
        }

        if (evt.type === 'frame') {
          const pSpeech =
            typeof evt?.probabilities?.speech === 'number'
              ? evt.probabilities.speech
              : typeof evt?.probabilities?.isSpeech === 'number'
                ? evt.probabilities.isSpeech
                : null

          yield* updateHearingFromProb(pSpeech)
          continue
        }

        if (evt.type !== 'speechStart') {
          continue
        }

        // segment mode
        let chunks = []
        const segmentStartedAt = Date.now()
        log('speechStart -> segment mode')

        while (true) {
          const { nextEvt, timedOut, stopped2 } = yield race({
            nextEvt: take(chan),
            timedOut: delay(MAX_AUDIO_LENGTH),
            stopped2: take(AUDIO_STOP),
          })

          if (stopped2) return
          if (nextEvt === END) {
            log('VAD channel END received (segment)')
            return
          }
          if (!nextEvt && !timedOut) continue

          const enabledSeg = yield select((s) => s.getIn(['audio', 'enabled']))
          const vadEnabledSeg = yield select((s) => s.getIn(['audio', 'vadEnabled']))
          if (!enabledSeg || !vadEnabledSeg) return

          const robotSpeakingSeg = yield select((s) => s.getIn(['audio', 'robotSpeaking']))
          if (robotSpeakingSeg) {
            // Robot started talking mid-segment -> discard segment.
            yield* resetHearing()
            log('robotSpeaking=true mid-segment -> discard segment')
            break
          }

          if (timedOut) {
            const floatAudio =
              chunks.length > 0 ? sttPrivate.concatFloat32Chunks(chunks) : new Float32Array(0)
            log('segment timeout -> dispatch segment', {
              ms: Date.now() - segmentStartedAt,
              samples: floatAudio.length,
            })
            yield put(audioSpeechSegmentReady(floatAudio))
            break
          }

          if (nextEvt.type === 'error') {
            yield put(audioError(nextEvt.error))
            return
          }

          if (nextEvt.type === 'frame') {
            frameSeen++

            if (Date.now() - lastFrameLog > 10000) {
              log('frames seen (10s)', frameSeen, 'used', frameUsed)
              frameSeen = 0
              frameUsed = 0
              lastFrameLog = Date.now()
            }

            chunks.push(nextEvt.frame)
            frameUsed++

            const pSpeech =
              typeof nextEvt?.probabilities?.speech === 'number'
                ? nextEvt.probabilities.speech
                : typeof nextEvt?.probabilities?.isSpeech === 'number'
                  ? nextEvt.probabilities.isSpeech
                  : null

            yield* updateHearingFromProb(pSpeech)
            continue
          }

          if (nextEvt.type === 'speechEnd') {
            const floatAudio =
              nextEvt.floatAudio ||
              (chunks.length > 0 ? sttPrivate.concatFloat32Chunks(chunks) : new Float32Array(0))
            log('speechEnd -> dispatch segment', {
              ms: Date.now() - segmentStartedAt,
              samples: floatAudio.length,
            })
            yield put(audioSpeechSegmentReady(floatAudio))
            break
          }
        }

        log('leaving segment mode')
      }
    } finally {
      log('controller saga finally: closing channel')
      try {
        chan.close()
      } catch (e) {
        log('channel close error', e)
      }
      yield put(audioStop())
      yield put(audioSetHearing('silence'))
    }
  }
}


function* transcribeSegmentSaga({ floatAudio }) {
  try {
    const robotSpeaking = yield select((s) => s.getIn(['audio', 'robotSpeaking'])) // NEW
    if (robotSpeaking) return // NEW

    const enabled = yield select((s) => s.getIn(['audio', 'enabled']))
    const sttEnabled = yield select((s) => s.getIn(['audio', 'sttEnabled']))
    if (!enabled || !sttEnabled) {
      return
    }

    yield put(audioTranscribeRequest())

    const form = makeTranscriptionForm(floatAudio)
    const transcriptObj = yield call(apiServer.postWithFetch, '/audioTranscribe', form)
    yield put(audioTranscribeResult(transcriptObj))

    const { isOk, text, message } = transcriptObj || {}
    if (!isOk) {
      if (AUDIO_DEBUG) console.warn('Audio transcription failed:', message)
      return
    }

    let transcript = text || ''
    const transcriptClean = normalizeTranscript(transcript)

    if (shouldStopMicByVoiceCommand(transcriptClean)) {
      // Instead of sessionStorage hard-disable, switch off at runtime:
      yield put(audioSetEnabled(false))
      yield put(audioSetVadEnabled(false))
      yield put(audioSetSttEnabled(false))
      yield put(audioStop())
      transcript += ' הפעולה בוצעה '
    }

    // Feed into your existing pipeline:
    yield put(userSaidAction(transcript))
  } catch (e) {
    yield put(audioError(e))
  }
}

function* robotSpeakingGateSaga() {
  while (true) {
    const action = yield take(AUDIO_SET_ROBOT_SPEAKING)
    const speaking = !!action.speaking

    // Do NOT stop audio / VAD here.
    // Optionally keep UI consistent:
    if (speaking) {
      yield put(audioSetHearing('silence'))
    }
  }
}

let initThemeTimer = null
let hasWarnedAboutMacOSTheme = false
let hasWarnedAboutCalendarTheme = false
// if it happens that I hear a double theme, it may be related to webpack-hot-refresh
// that messes the timers.  the test is only in light of no webpack-hot-refresh
const triggerThemeTimeout = async () => {
  if (isMacOS()) {
    if (!hasWarnedAboutMacOSTheme) {
      hasWarnedAboutMacOSTheme = true
      console.warn('not playing theme on macOS')
    }
    return
  }
  const now = new Date();
  const themeAllowedAfter = new Date('2026-02-01T00:00:00');
  if (now < themeAllowedAfter) {
    if (!hasWarnedAboutCalendarTheme) {
      hasWarnedAboutCalendarTheme = true
      console.warn('not playing theme before 1-Feb-26')
    }
    return
  }
  clearTimeout(initThemeTimer)
  initThemeTimer = setTimeout(async () => {
    await themeStart(IS_MOBILE_DEVICE)
    await triggerThemeTimeout()  // play theme again after it ends and some time passed
  }, TIME_TO_INIT_THEME_AFTER_KEYSTROKE)
}

function* playKeystrokeSaga({isHumanKey = false}) {
  try {
    yield call(themeStop)
    yield call(triggerThemeTimeout)
    if (!isHumanKey) {
      if (typeof tts === 'undefined')
        keyStrokePlay(IS_MOBILE_DEVICE)
    }
  } catch (e) {
    console.log('error in playKeystrokeSaga', {e})
  }
}


function* onAppLoadSaga() {
  console.log('onAppLoadSaga - audio')
  yield call(triggerThemeTimeout)
  yield put(audioStart())
  tts = yield call(startTTSEngine, {
    rate: 1,
    idleFlushMs: 220,
    maxChunkChars: 140,
    onRobotSpeakingChange: (speaking) => store.dispatch(audioSetRobotSpeaking(!!speaking)), // NEW
  })
  if (tts) {
    console.log('tts engine connected')
  }
}

function* acceptSpeakableTokensSaga() {
  while (true) {
    const action = yield take(TTS_SPEAK_TOKEN)
    const {token, shouldFlush} = action
    if (tts && token) {
      yield call(tts.pushToken, token)
    }
    if (shouldFlush) {
      yield call(tts.flush)
    }
  }
}

function* watchVariousSagas() {
  yield takeEvery(ON_APP_LOAD, onAppLoadSaga)
  yield takeLatest(USER_SAID, userSaidSaga)
  yield takeEvery(PLAY_KEYSTROKE, playKeystrokeSaga)
  yield takeEvery(AUDIO_SPEECH_SEGMENT_READY, transcribeSegmentSaga)
  yield fork(robotSpeakingGateSaga)
  yield fork(audioControllerSaga)
  yield fork(acceptSpeakableTokensSaga)
}

export default function* rootSaga() {
  yield all([watchVariousSagas()])
}
